import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../security/security/AuthContext';
import { checkUserRole } from '../security/api/ApiClient';
import Message from '../userDashboard/auth/loaders/Message';
import Loading from '../userDashboard/auth/loaders/Loading';

const AdminLogin = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const navigate = useNavigate();
  const authContext = useAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const validate = () => {
    const errors = {};
    if (!form.email) {
      errors.email = 'Email Address is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = 'Email Address is invalid';
    }
    if (!form.password) {
      errors.password = 'Password is required';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        const result = await authContext.login(form.email, form.password);
        if (result.success) {
          try {
            const role = await checkUserRole();
            console.log('User role:', role);
            if (role === 'ADMIN') {
              setMessage('Login successful! Redirecting to admin dashboard...');
              setMessageType('success');
              setTimeout(() => {
                navigate('/main/admindashboard/main');
              }, 2000);
            } else {
              setMessage('You do not have admin privileges.');
              setMessageType('error');
            }
          } catch (roleError) {
            console.error('Error checking user role:', roleError);
            setMessage('Error verifying admin privileges. Please try again.');
            setMessageType('error');
          }
        } else {
          setMessage(result.message);
          setMessageType('error');
        }
      } catch (error) {
        console.error('Login error:', error);
        setMessage('An unexpected error occurred. Please try again.');
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-wrapper">
        <div className="admin-login-card">
          <div className="admin-header">
            <div className="admin-logo">
              <div className="logo-circle">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h1 className="admin-title">Admin Portal</h1>
            <p className="admin-subtitle">Sign in to access the admin dashboard</p>
          </div>

          {message && <Message type={messageType} message={message} />}

          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="form-field">
              <label className="form-label">Email Address</label>
              <div className="input-group">
                <div className="input-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-field">
              <label className="form-label">Password</label>
              <div className="input-group">
                <div className="input-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 11H5C3.89543 11 3 11.8954 3 13V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2047 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.572 9.14351 13.1984C8.99262 12.8248 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2218 9.18488 10.8538C9.34884 10.4859 9.58525 10.1546 9.88 9.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? <Loading /> : 'Sign In'}
            </button>
          </form>

          <div className="security-badge">
            <div className="security-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>Secured Connection</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        .admin-login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: 'Inter', system-ui, sans-serif;
          position: relative;
          overflow: hidden;
        }

        .admin-login-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(ellipse at center, rgba(231, 76, 60, 0.1), transparent 70%);
          pointer-events: none;
        }

        .admin-login-wrapper {
          width: 100%;
          max-width: 420px;
          position: relative;
          z-index: 1;
        }

        .admin-login-card {
          background: linear-gradient(135deg, rgba(23, 42, 69, 0.95), rgba(16, 20, 31, 0.95));
          backdrop-filter: blur(20px);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 24px;
          padding: 40px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
          position: relative;
          overflow: hidden;
        }

        .admin-login-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #e74c3c, transparent);
        }

        .admin-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .admin-logo {
          margin-bottom: 24px;
        }

        .logo-circle {
          width: 72px;
          height: 72px;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          box-shadow: 0 12px 32px rgba(231, 76, 60, 0.3);
          color: white;
          position: relative;
        }

        .logo-circle::after {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border-radius: 50%;
          border: 2px solid rgba(231, 76, 60, 0.3);
          animation: logo-pulse 3s infinite;
        }

        @keyframes logo-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.5; }
        }

        .logo-circle svg {
          width: 32px;
          height: 32px;
        }

        .admin-title {
          font-size: 28px;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 8px 0;
          background: linear-gradient(135deg, #ffffff, #e74c3c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .admin-subtitle {
          font-size: 16px;
          color: #8892b0;
          margin: 0;
        }

        .admin-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin-bottom: 32px;
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-size: 14px;
          font-weight: 500;
          color: #e2e8f0;
        }

        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 16px;
          width: 20px;
          height: 20px;
          color: #64748b;
          z-index: 2;
        }

        .input-icon svg {
          width: 20px;
          height: 20px;
        }

        .form-input {
          width: 100%;
          background: rgba(16, 20, 31, 0.6);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 12px;
          padding: 16px 16px 16px 48px;
          font-size: 15px;
          color: #ffffff;
          font-family: inherit;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .form-input:focus {
          outline: none;
          border-color: #e74c3c;
          box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
          background: rgba(16, 20, 31, 0.8);
        }

        .form-input:focus + .password-toggle {
          color: #e74c3c;
        }

        .form-input::placeholder {
          color: #64748b;
        }

        .password-toggle {
          position: absolute;
          right: 16px;
          background: none;
          border: none;
          width: 20px;
          height: 20px;
          color: #64748b;
          cursor: pointer;
          transition: color 0.2s ease;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-toggle:hover {
          color: #e74c3c;
        }

        .password-toggle svg {
          width: 20px;
          height: 20px;
        }

        .error-text {
          font-size: 12px;
          color: #ef4444;
          margin-top: 4px;
        }

        .submit-button {
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border: none;
          border-radius: 12px;
          padding: 16px;
          font-size: 16px;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(231, 76, 60, 0.3);
        }

        .submit-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .submit-button:hover::before {
          left: 100%;
        }

        .submit-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(231, 76, 60, 0.4);
        }

        .submit-button:active {
          transform: translateY(0);
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .submit-button:disabled:hover {
          transform: none;
          box-shadow: 0 8px 24px rgba(231, 76, 60, 0.3);
        }

        .security-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #8892b0;
          font-size: 13px;
          padding-top: 20px;
          border-top: 1px solid rgba(231, 76, 60, 0.1);
        }

        .security-icon {
          width: 16px;
          height: 16px;
          color: #10b981;
        }

        .security-icon svg {
          width: 16px;
          height: 16px;
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          .admin-login-container {
            padding: 16px;
          }

          .admin-login-card {
            padding: 32px 24px;
            border-radius: 20px;
          }

          .logo-circle {
            width: 64px;
            height: 64px;
          }

          .logo-circle svg {
            width: 28px;
            height: 28px;
          }

          .admin-title {
            font-size: 24px;
          }

          .admin-subtitle {
            font-size: 14px;
          }

          .form-input {
            padding: 14px 14px 14px 44px;
            font-size: 14px;
          }

          .input-icon {
            left: 14px;
          }

          .password-toggle {
            right: 14px;
          }

          .submit-button {
            padding: 14px;
            font-size: 15px;
          }
        }

        @media (max-width: 360px) {
          .admin-login-card {
            padding: 28px 20px;
          }

          .admin-header {
            margin-bottom: 28px;
          }

          .admin-form {
            gap: 20px;
            margin-bottom: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;
